import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, FileText } from "lucide-react";
import { Card, PageHeader, Btn, Field, inputCls, Empty } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const categories = ["Logo", "Products", "Food", "Location", "Team", "Customers", "Services", "Before & After", "Videos", "Promotions", "Other"];
const accept = ".jpg,.jpeg,.png,.webp,.mp4,.mov,.pdf";
const MAX = 6 * 1024 * 1024;

export default function UploadMedia() {
  const [rows, setRows] = useState([]);
  const [category, setCategory] = useState("Products");
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const load = () => api.get("/media").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    const list = Array.from(files);
    for (let i = 0; i < list.length; i += 1) {
      const file = list[i];
      if (file.size > MAX) {
        toast.error(`${file.name} is larger than 6MB.`);
        continue;
      }
      setProgress({ name: file.name, pct: 10, index: i + 1, total: list.length });
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        setProgress((p) => ({ ...p, pct: 55 }));
        await api.post("/media", {
          filename: file.name,
          category,
          mime_type: file.type,
          size: file.size,
          data_url: dataUrl,
          description: "",
        });
        setProgress((p) => ({ ...p, pct: 100 }));
        toast.success(`${file.name} uploaded.`);
      } catch (e) {
        toast.error(errMessage(e));
      }
    }
    setProgress(null);
    load();
  };

  const saveDescription = async (id, description) => {
    try {
      await api.put(`/media/${id}`, { description });
      toast.success("Description saved.");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("File removed.");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="upload-media-page">
      <PageHeader
        title="Upload Media"
        body="Add photos, videos, logos, menus and other materials. Give each item a short description so we know what it shows."
      />

      <Card testId="upload-card">
        <Field label="Category for the next upload">
          <select data-testid="media-category-select" className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </Field>

        <div
          data-testid="media-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
          className={`mt-5 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
            dragging ? "border-[#F95738] bg-[#FFF7F4]" : "border-slate-300 bg-slate-50"
          }`}
        >
          <UploadCloud className="h-8 w-8 text-slate-400" aria-hidden="true" />
          <p className="mt-3 text-sm font-semibold text-slate-800">Drag and drop your files here</p>
          <p className="mt-1 text-xs text-slate-500">JPG, JPEG, PNG, WEBP, MP4, MOV, PDF · max 6MB per file</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={accept}
            data-testid="media-file-input"
            className="hidden"
            onChange={(e) => upload(e.target.files)}
          />
          <Btn testId="media-browse-button" className="mt-5" onClick={() => inputRef.current?.click()}>
            Choose files
          </Btn>
        </div>

        {progress && (
          <div className="mt-4" data-testid="upload-progress">
            <p className="text-xs text-slate-600">
              Uploading {progress.name} ({progress.index}/{progress.total})
            </p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full bg-[#F95738] transition-all" style={{ width: `${progress.pct}%` }} />
            </div>
          </div>
        )}
      </Card>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Your library ({rows.length})</p>
        {rows.length === 0 ? (
          <Empty testId="media-empty" title="No files yet" body="Upload your first photos so we can start building content." />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="media-list">
            {rows.map((m) => (
              <Card key={m.id} testId={`media-item-${m.id}`}>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{m.filename}</p>
                  <Btn variant="danger" testId={`media-delete-${m.id}`} onClick={() => remove(m.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Btn>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {m.category} · {(m.size / 1024).toFixed(0)} KB
                </p>
                <textarea
                  rows={2}
                  data-testid={`media-description-${m.id}`}
                  className={`mt-3 ${inputCls} text-sm`}
                  placeholder="New summer cocktail — Mojito Passion — €9.50"
                  defaultValue={m.description}
                  onBlur={(e) => saveDescription(m.id, e.target.value)}
                />
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
